import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IoIosArrowDown } from 'react-icons/io';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAppearances, getGoldRate } from '../../../../redux/homeSlice';
import { setVisible } from '../../../../redux/authModelSlice';
import Goldrate from '../../Goldrate/Goldrate';
import styles from './hamburgerMenu.module.css';

const HamburgerMenu = ({ headerRef }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { goldRateData, appearanceData } = useSelector((state) => state.appearances);
    const { isLoggedIn, user } = useSelector((state) => state.user);
    const categoriesData = appearanceData?.categories;
    const menuId = useId();
    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const focusOnOpenRef = useRef(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);
    const [popupPosition, setPopupPosition] = useState(null);

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
        setActiveCategory(null);
        setPopupPosition(null);
        focusOnOpenRef.current = false;
    }, []);

    const openMenu = () => {
        focusOnOpenRef.current = true;
        setIsMenuOpen(true);
    };

    useEffect(() => {
        closeMenu();
    }, [location, closeMenu]);

    useLayoutEffect(() => {
        if (!isMenuOpen) return;

        const updatePosition = () => {
            if (window.innerWidth > 1180) {
                closeMenu();
                return;
            }

            const header = headerRef.current;
            const trigger = triggerRef.current;
            if (!header || !trigger) return;

            const viewport = window.visualViewport;
            const viewportLeft = viewport?.offsetLeft || 0;
            const viewportTop = viewport?.offsetTop || 0;
            const viewportWidth = viewport?.width || document.documentElement.clientWidth;
            const viewportHeight = viewport?.height || window.innerHeight;
            const width = Math.min(288, Math.max(0, viewportWidth - 24));
            const top = Math.max(header.getBoundingClientRect().bottom + 8, viewportTop + 12);
            const left = Math.min(
                Math.max(trigger.getBoundingClientRect().left, viewportLeft + 12),
                viewportLeft + viewportWidth - width - 12
            );
            const maxHeight = Math.max(0, viewportTop + viewportHeight - top - 12);

            setPopupPosition((previous) => {
                if (previous?.top === top && previous?.left === left &&
                    previous?.width === width && previous?.maxHeight === maxHeight) {
                    return previous;
                }
                return { top, left, width, maxHeight };
            });
        };

        updatePosition();
        const observer = new ResizeObserver(updatePosition);
        if (headerRef.current) observer.observe(headerRef.current);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        window.visualViewport?.addEventListener('resize', updatePosition);
        window.visualViewport?.addEventListener('scroll', updatePosition);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            window.visualViewport?.removeEventListener('resize', updatePosition);
            window.visualViewport?.removeEventListener('scroll', updatePosition);
        };
    }, [isMenuOpen, headerRef, closeMenu]);

    useLayoutEffect(() => {
        if (isMenuOpen && popupPosition && focusOnOpenRef.current) {
            popupRef.current?.querySelector('a, button')?.focus({ preventScroll: true });
            focusOnOpenRef.current = false;
        }
    }, [isMenuOpen, popupPosition]);

    useEffect(() => {
        if (!isMenuOpen) return;

        const handleOutsideInteraction = (event) => {
            if (!popupRef.current?.contains(event.target) &&
                !triggerRef.current?.contains(event.target)) {
                closeMenu();
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeMenu();
                triggerRef.current?.focus({ preventScroll: true });
            }
        };

        document.addEventListener('pointerdown', handleOutsideInteraction);
        document.addEventListener('focusin', handleOutsideInteraction);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handleOutsideInteraction);
            document.removeEventListener('focusin', handleOutsideInteraction);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMenuOpen, closeMenu]);

    useEffect(() => {
        dispatch(getGoldRate());
        dispatch(getAppearances());
    }, [dispatch]);

    const handleAccountNavigation = (destination) => {
        closeMenu();
        if (!isLoggedIn) {
            dispatch(setVisible('signin'));
        } else {
            navigate(destination);
        }
    };

    const handleGoldrateClick = () => {
        closeMenu();
        setIsPopupVisible(true);
    };

    return (
        <div className={styles.hamburgerContainer}>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.hamburgerIcon} ${isMenuOpen ? styles.open : ''}`}
                aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isMenuOpen}
                aria-controls={isMenuOpen ? menuId : undefined}
                onClick={isMenuOpen ? closeMenu : openMenu}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        if (isMenuOpen) {
                            popupRef.current?.querySelector('a, button')?.focus();
                        } else {
                            openMenu();
                        }
                    }
                }}
            >
                <span className={styles.iconBars} aria-hidden="true">
                    <span className={styles.bar} />
                    <span className={styles.bar} />
                    <span className={styles.bar} />
                </span>
            </button>

            {isMenuOpen && popupPosition && createPortal(
                <nav
                    id={menuId}
                    ref={popupRef}
                    className={styles.navMenu}
                    style={popupPosition}
                    aria-label="Main navigation"
                >
                    <Link className={styles.menuItem} to="/" onClick={closeMenu}>Home</Link>

                    {categoriesData?.map((category) => {
                        const expanded = activeCategory === category._id;
                        const subcategoryId = `${menuId}-${category._id}`;
                        return (
                            <div key={category._id}>
                                <div className={styles.categoryRow}>
                                    <Link
                                        className={styles.menuItem}
                                        to={`/product/${category._id}`}
                                        state={category.name}
                                        onClick={closeMenu}
                                    >
                                        {category.name}
                                    </Link>
                                    {category.subcategories?.length > 0 && (
                                        <button
                                            type="button"
                                            className={styles.categoryToggle}
                                            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${category.name} subcategories`}
                                            aria-expanded={expanded}
                                            aria-controls={subcategoryId}
                                            onClick={() => setActiveCategory(expanded ? null : category._id)}
                                        >
                                            <IoIosArrowDown aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                                {category.subcategories?.length > 0 && (
                                    <div id={subcategoryId} className={styles.subcategories} hidden={!expanded}>
                                        {category.subcategories.map((subCategory) => (
                                            <Link
                                                key={subCategory._id}
                                                className={styles.subcategoryItem}
                                                to={`/productsWithSubCategory/${subCategory._id}`}
                                                state={category}
                                                onClick={closeMenu}
                                            >
                                                {subCategory.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className={styles.accountActions}>
                        <button type="button" className={styles.menuItem} onClick={() => handleAccountNavigation('/wishlist')}>
                            Wishlist
                            {user?.unreadWishlistCount > 0 && (
                                <span className={styles.badge}>{user.unreadWishlistCount}</span>
                            )}
                        </button>
                        <button type="button" className={styles.menuItem} onClick={() => handleAccountNavigation('/cart')}>
                            Cart
                            {user?.unreadCartCount > 0 && (
                                <span className={styles.badge}>{user.unreadCartCount}</span>
                            )}
                        </button>
                    </div>

                    <button type="button" className={`${styles.menuItem} ${styles.goldRate}`} onClick={handleGoldrateClick}>
                        Today's Gold Rate
                    </button>
                </nav>,
                document.body
            )}
            {isPopupVisible && (
                <Goldrate onClose={() => setIsPopupVisible(false)} goldRateData={goldRateData} />
            )}
        </div>
    );
};

export default HamburgerMenu;
